// src/Contexts/MidiContext.tsx
/* eslint-disable react-refresh/only-export-components -- context file */
import React, { useCallback } from 'react';
import { CalibrationPanel, MiddleCReference, useAudioRecognition } from 'AudioRecognition';
import type { AudioRecCalibrationProfile, RecognitionFrame } from 'AudioRecognition/types';
import { hzToMidi } from 'AudioRecognition/util/note';
import { detectDevice, midiPitchToFrequency } from 'Utils';
import { useEffect, useState, useRef } from 'react';
import * as eventAction from 'Actions/events';
import * as Tone from 'tone';
import { UseStateError } from 'Utils/Errors';
import { useSelector, useDispatch } from 'react-redux';
import { MainAppReducer } from 'Types';
import logger from 'Utils/Logger';
import { reportError } from 'Utils/reportError';
import { isCapacitorNative, MIDI_BLOCKER_PATHS } from 'Utils/Constants';
import { Box, IconButton } from '@mui/material';
import KeyboardConnectMidi from 'assets/images/KeyboardConnectMidi.png';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import * as lessonActions from 'Actions/lesson';
import * as tutorialActions from 'Actions/tutorial';
import * as repertoireActions from 'Actions/repertoire';
import * as appActions from 'Actions/app';
import { isSafariBrowser } from 'Utils/UserAgent';
import { awsRum } from 'Utils/AwsRum';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import { Button } from '@mui/material';

const Logger = logger;

// Define NoteMessage type (was from @midival/core)
export type NoteMessage = {
  note: number;
  velocity: number;
  channel: number;
  command?: number;
  data1?: number;
  data2?: number;
  source?: 'midi' | 'audio-recognition';
};

export type PedalMessage = {
  channel: number;
  controller: number;
  value: number;
  down: boolean;
  command?: number;
  data1?: number;
  data2?: number;
};

// Type for tracking connected MIDI inputs
type MidiInputInfo = {
  id: string;
  name: string;
  input: MIDIInput;
};

// Global MIDI state that persists across React re-renders/refreshes
// This prevents the hanging issue where requestMIDIAccess never resolves on refresh
let globalMidiAccess: MIDIAccess | null = null;
let globalMidiPromise: Promise<MIDIAccess> | null = null;
/// Change the input on and off events to be react callbacks
// so that a change in state playToneSound will be reflected
// Will need to add methods to change playToneSound similar to
// functions defined in Authentication.tsx

export type MidiProviderProps = {
  children?: React.ReactNode;
  location?: { pathname: string };
  navigate?: (path: string) => void;
  sampler?: Tone.Sampler;
};

export type MidiStateType = {
  showMidiModal: boolean;
  dismissedMidiModal: boolean;
};

export type MidiFunctions = {
  addListener: (listener: Listener) => void;
  removeListener: (listener: Listener) => void;
  clearListeners: () => void;
  isConnected: () => boolean;
  capMidiReceived: { on: string; off: string; other: unknown; connected: boolean };
  checkMidiAndShowModal: () => void;
  openInputSetupModal: () => void;
  hideMidiModal: () => void;
  audioInputLatencyMs: number;
  preferredInputMode: PreferredInputMode;
  setPreferredInputMode: (mode: PreferredInputMode) => void;
};

export type Listener = {
  name: string;
  noteOn: (ev: NoteMessage) => void;
  noteOff: (ev: NoteMessage) => void;
  pedalChange?: (ev: PedalMessage) => void;
};

export const MidiContext = React.createContext<(MidiStateType & MidiFunctions) | null>(null);

export const useMidiContext: () => MidiStateType & MidiFunctions = () => {
  const state = React.useContext(MidiContext);
  if (!state) {
    throw new UseStateError(useMidiContext, MidiProvider);
  }
  return {
    ...state,
  };
};

type MidiDeviceMap = { [key: string]: MidiInputInfo };

const AUDIO_RECOGNITION_INPUT_LATENCY_MS = 93;
const DEFAULT_AUDIO_NOTE_ON_STABILITY_MS = 80;
const DEFAULT_AUDIO_NOTE_OFF_SILENCE_MS = 360;
const DEFAULT_AUDIO_NOTE_MIN_HOLD_MS = 280;
const AUDIO_NOTE_OFF_SILENCE_FLOOR_MS = 360;
const AUDIO_NOTE_OFF_SILENCE_EXTRA_MS = 140;
const AUDIO_NOTE_OFF_TAIL_EXTENSION_MS = 220;
const AUDIO_NOTE_OFF_TAIL_MAX_MS = 900;
const SYNTHETIC_AUDIO_NOTE_VELOCITY = 100;
const AUDIO_BRIDGE_ON_RMS_MARGIN = 1.0;
const AUDIO_BRIDGE_KEEP_RMS_MARGIN = 0.72;
const AUDIO_BRIDGE_KEEP_RMS_CALIBRATION_RATIO = 0.82;
const AUDIO_BRIDGE_RELEASE_TAIL_RMS_MARGIN = 0.5;
const AUDIO_BRIDGE_MIN_PEAK_RATIO = 0.42;
const AUDIO_CALIBRATION_COMPLETE_MODAL_CLOSE_MS = 2200;
const AUDIO_BRIDGE_MAX_NOTES = 6;
const AUDIO_BRIDGE_ABSOLUTE_MIN_RMS = 0.0024;
const AUDIO_BRIDGE_ABSOLUTE_KEEP_RMS = 0.0016;
const AUDIO_BRIDGE_ABSOLUTE_TAIL_RMS = 0.0012;
const AUDIO_BRIDGE_ABSOLUTE_SELECTED_KEEP_RMS = 0.00012;
const INPUT_MODE_STORAGE_KEY = 'preferred-input-mode-v1';
const PREFERRED_INPUT_SYNC_GRACE_MS = 8000;

export type PreferredInputMode = 'midi' | 'audio';

const readStoredPreferredInputMode = (): PreferredInputMode | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(INPUT_MODE_STORAGE_KEY);
  if (stored === 'audio') return 'audio';
  if (stored === 'midi') return 'midi';
  return null;
};

const readPreferredInputMode = (): PreferredInputMode => {
  return readStoredPreferredInputMode() ?? 'midi';
};

const toPreferredInputMode = (preferAudioRecognitionInput?: boolean | null): PreferredInputMode =>
  preferAudioRecognitionInput ? 'audio' : 'midi';

const shouldDebugMidiGate = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_NODE_ENV === 'local' ||
    process.env.REACT_APP_NODE_ENV === 'dev' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    Boolean((window as Window & { __DEBUG_MIDI_GATE?: boolean }).__DEBUG_MIDI_GATE)
  );
};

const toSyntheticNoteMessage = (
  note: number,
  velocity: number,
  command: number,
  source: NoteMessage['source'] = 'audio-recognition',
): NoteMessage => ({
  note,
  velocity,
  channel: 0,
  command,
  data1: note,
  data2: velocity,
  source,
});

const SUSTAIN_PEDAL_CONTROLLER = 64;
const SUSTAIN_PEDAL_ON_VALUE = 64;

const quantizeDetectedNotes = (hzs: number[]) => {
  const midiNotes = new Set<number>();
  for (const hz of hzs) {
    if (!Number.isFinite(hz) || hz <= 0) continue;
    const midiNote = Math.round(hzToMidi(hz));
    if (midiNote >= 0 && midiNote <= 127) {
      midiNotes.add(midiNote);
    }
  }
  return midiNotes;
};

const getBridgeDetectedNotes = (
  frame: RecognitionFrame,
  calibrationProfile: AudioRecCalibrationProfile | null,
) => {
  const rms = frame.rms ?? 0;
  const selectedMidiNotes = Array.from(
    new Set((frame.selectedNotes ?? []).map((note) => Math.round(note.midi))),
  )
    .filter((note) => note >= 0 && note <= 127)
    .slice(0, AUDIO_BRIDGE_MAX_NOTES);
  const stableMidiNotes = Array.from(
    new Set((frame.midiNotes ?? []).map((note) => Math.round(note))),
  )
    .filter((note) => note >= 0 && note <= 127)
    .slice(0, AUDIO_BRIDGE_MAX_NOTES);
  const stableCandidateHzs = frame.hzs.slice(0, AUDIO_BRIDGE_MAX_NOTES);
  const minRms = frame.minRms ?? 0;

  if (selectedMidiNotes.length > 0) {
    const dynamicSelectedKeepMinRms = minRms > 0 ? minRms * AUDIO_BRIDGE_KEEP_RMS_MARGIN : 0;
    const selectedKeepMinRms = Math.max(
      AUDIO_BRIDGE_ABSOLUTE_SELECTED_KEEP_RMS,
      dynamicSelectedKeepMinRms,
    );

    if (!Number.isFinite(rms) || rms < selectedKeepMinRms) {
      return new Set<number>();
    }

    return new Set<number>(selectedMidiNotes);
  }

  if (stableMidiNotes.length > 0 || stableCandidateHzs.length > 0) {
    const dynamicKeepMinRms = minRms > 0 ? minRms * AUDIO_BRIDGE_KEEP_RMS_MARGIN : 0;
    const calibratedKeepMinRms =
      (calibrationProfile?.bridge.keepRmsBase ?? 0) * AUDIO_BRIDGE_KEEP_RMS_CALIBRATION_RATIO;
    const bridgeKeepMinRms = Math.max(
      AUDIO_BRIDGE_ABSOLUTE_KEEP_RMS,
      dynamicKeepMinRms,
      calibratedKeepMinRms,
    );

    if (!Number.isFinite(rms) || rms < bridgeKeepMinRms) {
      return new Set<number>();
    }

    if (stableMidiNotes.length > 0) {
      return new Set<number>(stableMidiNotes);
    }

    return quantizeDetectedNotes(stableCandidateHzs);
  }

  const dynamicMinRms = minRms > 0 ? minRms * AUDIO_BRIDGE_ON_RMS_MARGIN : 0;
  const calibratedMinRms = calibrationProfile?.bridge.onRmsBase ?? 0;
  const bridgeMinRms = Math.max(AUDIO_BRIDGE_ABSOLUTE_MIN_RMS, dynamicMinRms, calibratedMinRms);

  if (!Number.isFinite(rms) || rms < bridgeMinRms) {
    return new Set<number>();
  }

  const strongestAmp = frame.peaks?.reduce((max, peak) => Math.max(max, peak.amp), 0) ?? 0;
  const strongPeakHzs =
    strongestAmp > 0
      ? (frame.peaks ?? [])
          .filter((peak) => peak.amp >= strongestAmp * AUDIO_BRIDGE_MIN_PEAK_RATIO)
          .slice(0, AUDIO_BRIDGE_MAX_NOTES)
          .map((peak) => peak.hz)
      : [];
  return quantizeDetectedNotes(strongPeakHzs.slice(0, AUDIO_BRIDGE_MAX_NOTES));
};

const getAudioNoteOffSilenceMs = (calibrationProfile: AudioRecCalibrationProfile | null) =>
  Math.max(
    AUDIO_NOTE_OFF_SILENCE_FLOOR_MS,
    (calibrationProfile?.bridge.offSilenceMs ?? DEFAULT_AUDIO_NOTE_OFF_SILENCE_MS) +
      AUDIO_NOTE_OFF_SILENCE_EXTRA_MS,
  );

const getAudioNoteMinHoldMs = (calibrationProfile: AudioRecCalibrationProfile | null) =>
  Math.max(
    DEFAULT_AUDIO_NOTE_MIN_HOLD_MS,
    calibrationProfile?.bridge.minHoldMs ?? DEFAULT_AUDIO_NOTE_MIN_HOLD_MS,
  );

const hasBridgeReleaseTailSignal = (
  frame: RecognitionFrame,
  calibrationProfile: AudioRecCalibrationProfile | null,
) => {
  const rms = frame.rms ?? 0;
  const minRms = frame.minRms ?? 0;
  const dynamicTailMinRms = minRms > 0 ? minRms * AUDIO_BRIDGE_RELEASE_TAIL_RMS_MARGIN : 0;
  const calibratedTailMinRms =
    (calibrationProfile?.bridge.keepRmsBase ?? 0) * AUDIO_BRIDGE_KEEP_RMS_CALIBRATION_RATIO;
  const tailMinRms = Math.max(
    AUDIO_BRIDGE_ABSOLUTE_TAIL_RMS,
    dynamicTailMinRms,
    calibratedTailMinRms,
  );

  return Number.isFinite(rms) && rms >= tailMinRms;
};

type AudioRecognitionMidiBridgeProps = {
  enabled: boolean;
  noteOnRef: React.MutableRefObject<(ev: NoteMessage) => void>;
  noteOffRef: React.MutableRefObject<(ev: NoteMessage) => void>;
  setAudioInputReady: React.Dispatch<React.SetStateAction<boolean>>;
  setAudioInputChecked: React.Dispatch<React.SetStateAction<boolean>>;
};

const AudioRecognitionMidiBridge: React.FC<AudioRecognitionMidiBridgeProps> = ({
  enabled,
  noteOnRef,
  noteOffRef,
  setAudioInputReady,
  setAudioInputChecked,
}) => {
  const { addListener, removeListener, start, stop, calibrationProfile } = useAudioRecognition();
  const bridgeStateRef = useRef<{
    activeNotes: Set<number>;
    seenSinceMs: Map<number, number>;
    missingSinceMs: Map<number, number>;
    activeSinceMs: Map<number, number>;
    lastSeenMs: Map<number, number>;
  }>({
    activeNotes: new Set<number>(),
    seenSinceMs: new Map<number, number>(),
    missingSinceMs: new Map<number, number>(),
    activeSinceMs: new Map<number, number>(),
    lastSeenMs: new Map<number, number>(),
  });

  const flushActiveNotes = useCallback(() => {
    const bridgeState = bridgeStateRef.current;
    for (const note of bridgeState.activeNotes) {
      noteOffRef.current(toSyntheticNoteMessage(note, 0, 0x80));
    }
    bridgeState.activeNotes.clear();
    bridgeState.seenSinceMs.clear();
    bridgeState.missingSinceMs.clear();
    bridgeState.activeSinceMs.clear();
    bridgeState.lastSeenMs.clear();
  }, [noteOffRef]);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      flushActiveNotes();
      setAudioInputReady(false);
      setAudioInputChecked(true);
      void stop();
      return;
    }

    setAudioInputChecked(false);

    start()
      .then(() => {
        if (cancelled) return;
        setAudioInputReady(true);
        setAudioInputChecked(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('[MidiContext] Audio recognition start failed:', error);
        flushActiveNotes();
        setAudioInputReady(false);
        setAudioInputChecked(true);
      });

    return () => {
      cancelled = true;
      flushActiveNotes();
      setAudioInputReady(false);
      setAudioInputChecked(true);
      void stop();
    };
  }, [enabled, flushActiveNotes, setAudioInputChecked, setAudioInputReady, start, stop]);

  useEffect(() => {
    if (!enabled) return;

    const noteOnStabilityMs = Math.max(
      30,
      Math.min(
        calibrationProfile?.bridge.onStabilityMs ?? DEFAULT_AUDIO_NOTE_ON_STABILITY_MS,
        DEFAULT_AUDIO_NOTE_ON_STABILITY_MS,
      ),
    );
    const noteOffSilenceMs = getAudioNoteOffSilenceMs(calibrationProfile);
    const noteMinHoldMs = getAudioNoteMinHoldMs(calibrationProfile);

    const listener = {
      name: 'audio-recognition-midi-bridge',
      onFrame: (frame: RecognitionFrame) => {
        const nowMs = frame.timeSec * 1000;
        const detectedNotes = getBridgeDetectedNotes(frame, calibrationProfile);
        const hasReleaseTailSignal = hasBridgeReleaseTailSignal(frame, calibrationProfile);
        const bridgeState = bridgeStateRef.current;

        for (const note of Array.from(bridgeState.activeNotes)) {
          if (detectedNotes.has(note)) {
            bridgeState.missingSinceMs.delete(note);
            bridgeState.lastSeenMs.set(note, nowMs);
            continue;
          }

          const missingSinceMs = bridgeState.missingSinceMs.get(note);
          if (missingSinceMs == null) {
            bridgeState.missingSinceMs.set(note, nowMs);
            continue;
          }

          const activeSinceMs =
            bridgeState.activeSinceMs.get(note) ??
            bridgeState.lastSeenMs.get(note) ??
            missingSinceMs;
          const lastSeenMs = bridgeState.lastSeenMs.get(note) ?? activeSinceMs;
          const noteAgeMs = nowMs - activeSinceMs;
          const tailAgeMs = nowMs - lastSeenMs;
          const releaseSilenceMs =
            hasReleaseTailSignal && tailAgeMs <= AUDIO_NOTE_OFF_TAIL_MAX_MS
              ? noteOffSilenceMs + AUDIO_NOTE_OFF_TAIL_EXTENSION_MS
              : noteOffSilenceMs;

          if (noteAgeMs >= noteMinHoldMs && nowMs - missingSinceMs >= releaseSilenceMs) {
            bridgeState.activeNotes.delete(note);
            bridgeState.missingSinceMs.delete(note);
            bridgeState.seenSinceMs.delete(note);
            bridgeState.activeSinceMs.delete(note);
            bridgeState.lastSeenMs.delete(note);
            noteOffRef.current(toSyntheticNoteMessage(note, 0, 0x80));
          }
        }

        for (const note of detectedNotes) {
          bridgeState.missingSinceMs.delete(note);

          if (bridgeState.activeNotes.has(note)) {
            bridgeState.seenSinceMs.delete(note);
            bridgeState.lastSeenMs.set(note, nowMs);
            continue;
          }

          const seenSinceMs = bridgeState.seenSinceMs.get(note);
          if (seenSinceMs == null) {
            bridgeState.seenSinceMs.set(note, nowMs);
            continue;
          }

          if (nowMs - seenSinceMs >= noteOnStabilityMs) {
            bridgeState.activeNotes.add(note);
            bridgeState.activeSinceMs.set(note, nowMs);
            bridgeState.lastSeenMs.set(note, nowMs);
            bridgeState.seenSinceMs.delete(note);
            noteOnRef.current(toSyntheticNoteMessage(note, SYNTHETIC_AUDIO_NOTE_VELOCITY, 0x90));
          }
        }

        for (const note of Array.from(bridgeState.seenSinceMs.keys())) {
          if (!detectedNotes.has(note) && !bridgeState.activeNotes.has(note)) {
            bridgeState.seenSinceMs.delete(note);
          }
        }
      },
    };

    addListener(listener);

    return () => {
      removeListener(listener);
      flushActiveNotes();
    };
  }, [
    addListener,
    calibrationProfile,
    enabled,
    flushActiveNotes,
    noteOffRef,
    noteOnRef,
    removeListener,
  ]);

  return null;
};

export const MidiProvider: React.FC<MidiProviderProps> = ({
  children,
  location,
  navigate,
  sampler,
}: MidiProviderProps) => {
  const { calibration, calibrationProfile } = useAudioRecognition();
  const [midiInitialized, setMidiInitialized] = useState(false);
  const [devices, setDevices] = useState<MidiDeviceMap>({});
  const [showMidiModal, setShowMidiModal] = useState(false);
  const [inputSetupModalPinnedOpen, setInputSetupModalPinnedOpen] = useState(false);
  const [dismissedMidiModal, setDismissedMidiModal] = useState(false); // user manually closed
  const [midiDevicesChecked, setMidiDevicesChecked] = useState(false); // true after MIDI init completes
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const [audioInputReady, setAudioInputReady] = useState(false);
  const [, setAudioInputChecked] = useState(!audioRecognitionEnabled);
  const [preferredInputModeState, setPreferredInputModeState] = useState<PreferredInputMode>(() =>
    readPreferredInputMode(),
  );
  const [preferredInputModeOverride, setPreferredInputModeOverride] =
    useState<PreferredInputMode | null>(null);
  const preferredInputModePendingRef = useRef<{
    mode: PreferredInputMode;
    requestedAtMs: number;
  } | null>(null);
  const storedPreferredInputMode = preferredInputModeOverride ?? preferredInputModeState;
  const preferredInputMode = audioRecognitionEnabled ? storedPreferredInputMode : 'midi';

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const listeners = React.useRef<Map<string, Listener>>(new Map());
  const dispatch = useDispatch();
  // Tracks whether MidiConnectionSuccessEvent has already been emitted for this
  // session. A ref (not state) so all emit sites — the onstatechange listener
  // attached inside an outer effect closure AND the init useEffect — read the
  // current value at fire time. If this were state, the onstatechange closure
  // would capture the initial `false` and the event would fire twice.
  const successEventSentRef = React.useRef(false);
  const restrict_midi = process.env.REACT_APP_REQUIRE_MIDI === 'true';

  // const audioReady = (window as any).__audioUnlocked === true && Tone.getContext().rawContext?.state === "running";
  const audioReadyRef = useRef(false);

  useEffect(() => {
    const update = () => {
      audioReadyRef.current =
        (window as Window & { __audioUnlocked?: boolean }).__audioUnlocked === true &&
        (Tone.getContext().rawContext as AudioContext | undefined)?.state === 'running';
    };

    update();

    const onAudioReady = (e: Event) => {
      audioReadyRef.current = !!(e as CustomEvent<{ ready?: boolean }>).detail?.ready;
    };

    window.addEventListener('AudioReady', onAudioReady);
    document.addEventListener('visibilitychange', update);

    return () => {
      window.removeEventListener('AudioReady', onAudioReady);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  useEffect(() => {
    if (!data.userDataFetched || !data.userData) return;

    const storedPreferredInputMode = readStoredPreferredInputMode();
    if (storedPreferredInputMode != null) {
      setPreferredInputModeState((currentPreferredInputMode) =>
        currentPreferredInputMode === storedPreferredInputMode
          ? currentPreferredInputMode
          : storedPreferredInputMode,
      );
      return;
    }

    const nextPreferredInputMode = toPreferredInputMode(
      data.userData.prefer_audio_recognition_input,
    );
    const pendingPreferredInputMode = preferredInputModePendingRef.current;

    if (pendingPreferredInputMode) {
      if (pendingPreferredInputMode.mode === nextPreferredInputMode) {
        preferredInputModePendingRef.current = null;
        setPreferredInputModeOverride(null);
      } else if (
        Date.now() - pendingPreferredInputMode.requestedAtMs <
        PREFERRED_INPUT_SYNC_GRACE_MS
      ) {
        return;
      } else {
        preferredInputModePendingRef.current = null;
        setPreferredInputModeOverride(null);
      }
    }

    setPreferredInputModeState((currentPreferredInputMode) =>
      currentPreferredInputMode === nextPreferredInputMode
        ? currentPreferredInputMode
        : nextPreferredInputMode,
    );
  }, [data.userData, data.userData?.prefer_audio_recognition_input, data.userDataFetched]);

  const setPreferredInputMode = useCallback(
    (mode: PreferredInputMode) => {
      const nextMode = audioRecognitionEnabled ? mode : 'midi';
      setPreferredInputModeState(nextMode);
      setPreferredInputModeOverride(nextMode);
      preferredInputModePendingRef.current = {
        mode: nextMode,
        requestedAtMs: Date.now(),
      };
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(INPUT_MODE_STORAGE_KEY, nextMode);
        } catch {
          // Ignore storage failures.
        }
      }
      if (!data.userDataFetched || !data.userData) return;

      const preferAudioRecognitionInput = nextMode === 'audio';
      if (data.userData.prefer_audio_recognition_input === preferAudioRecognitionInput) {
        preferredInputModePendingRef.current = null;
        setPreferredInputModeOverride(null);
        return;
      }

      dispatch(
        appActions.updateUserData({
          prefer_audio_recognition_input: preferAudioRecognitionInput,
        }),
      );
    },
    [audioRecognitionEnabled, data.userData, data.userDataFetched, dispatch],
  );

  const sustainedNotesRef = useRef<Set<number>>(new Set());
  const activeNotesRef = useRef<Set<number>>(new Set());
  const sustainPedalDownRef = useRef(false);

  const triggerSamplerRelease = useCallback(
    (note: number) => {
      const audioOn = data?.userData?.audio_on !== undefined ? data.userData.audio_on : true;
      if (audioOn && audioReadyRef.current && !isCapacitorNative()) {
        try {
          sampler?.triggerRelease(midiPitchToFrequency(note));
        } catch (error) {
          Logger.warn('Failed to trigger sampler release:', error);
          awsRum?.recordError(error as Error);
        }
      }
    },
    [data?.userData?.audio_on, sampler],
  );

  const shouldTriggerSamplerAttack = useCallback(
    (ev: NoteMessage) => ev.source !== 'audio-recognition' && preferredInputMode !== 'audio',
    [preferredInputMode],
  );

  useEffect(() => {
    if (preferredInputMode !== 'audio') return;

    const notesToRelease = new Set([
      ...Array.from(activeNotesRef.current),
      ...Array.from(sustainedNotesRef.current),
    ]);
    notesToRelease.forEach((note) => triggerSamplerRelease(note));
    sustainedNotesRef.current.clear();
  }, [preferredInputMode, triggerSamplerRelease]);

  const releaseSustainedNotes = useCallback(() => {
    const sustainedNotes = Array.from(sustainedNotesRef.current);
    sustainedNotes.forEach((note) => {
      if (!activeNotesRef.current.has(note)) {
        triggerSamplerRelease(note);
        sustainedNotesRef.current.delete(note);
      }
    });
  }, [triggerSamplerRelease]);

  const pedalChange = React.useCallback(
    (ev: PedalMessage) => {
      const wasDown = sustainPedalDownRef.current;
      sustainPedalDownRef.current = ev.down;

      if (wasDown && !ev.down) {
        releaseSustainedNotes();
      }

      for (const [, listener] of listeners.current) {
        listener.pedalChange?.(ev);
      }
    },
    [listeners, releaseSustainedNotes],
  );

  const noteOff = React.useCallback(
    (ev: NoteMessage) => {
      activeNotesRef.current.delete(ev.note);
      const shouldPlayLocalSound = ev.source !== 'audio-recognition';
      if (sustainPedalDownRef.current) {
        sustainedNotesRef.current.add(ev.note);
      } else {
        sustainedNotesRef.current.delete(ev.note);
        if (shouldPlayLocalSound) {
          triggerSamplerRelease(ev.note);
        }
      }
      for (const [, listener] of listeners.current) {
        listener.noteOff(ev);
      }
    },
    [listeners, triggerSamplerRelease],
  );

  const noteOn = React.useCallback(
    (ev: NoteMessage) => {
      // sometimes midi keyboards will send note-on event with 0 velocity
      // we must treat this like a not off event instead
      // 254 is "active sensing" and can be ignored
      // Only play sound if audio_on is explicitly true (not false or undefined)
      // Default to true if undefined (matches UI default), but respect false
      const audioOn = data?.userData?.audio_on !== undefined ? data.userData.audio_on : true;
      // NOTE: We intentionally do NOT write a per-note debug string into
      // capMidiReceived state here. Doing so re-rendered the entire
      // MidiProvider subtree (which wraps every route, including the
      // interactive video tutorial's <ReactPlayer>) on every single note.
      // During the interactive video tutorials that re-render storm starved
      // the main thread, causing the tutorial video to jitter and stall when
      // the user played along (MUS-280). Only `connected` is ever consumed
      // from capMidiReceived; the `on`/`off`/`other` debug fields are not.

      if (ev.velocity !== 0 && ev.note !== 254) {
        Logger.log(
          '[MidiContext] noteOn callback - note:',
          ev.note,
          'velocity:',
          ev.velocity,
          'listeners:',
          listeners.current.size,
        );
        const maxVolume = 1.0;
        const shouldReleaseLocalSound = ev.source !== 'audio-recognition';
        const shouldPlayLocalSound = shouldTriggerSamplerAttack(ev);
        activeNotesRef.current.add(ev.note);
        if (sustainedNotesRef.current.has(ev.note)) {
          if (shouldReleaseLocalSound) {
            triggerSamplerRelease(ev.note);
          }
          sustainedNotesRef.current.delete(ev.note);
        }
        // Only play sound if audio_on is explicitly true
        if (shouldPlayLocalSound && audioOn && audioReadyRef.current && !isCapacitorNative()) {
          try {
            sampler?.triggerAttack(
              midiPitchToFrequency(ev.note),
              Tone.immediate(),
              (ev.velocity / 128) * maxVolume,
            );
          } catch (error) {
            Logger.warn('Failed to trigger sampler attack:', error);
            awsRum?.recordError(error as Error);
          }
        }
        for (const [name, listener] of listeners.current) {
          Logger.log('[MidiContext] Calling listener:', name);
          listener.noteOn(ev);
        }
      } else if (ev.note !== 254) {
        noteOffRef.current(ev); // call the note off logic becasue velocity is 0.
      }
    },
    [
      data?.userData?.audio_on,
      sampler,
      noteOff,
      listeners,
      shouldTriggerSamplerAttack,
      triggerSamplerRelease,
    ],
  );

  const noteOnRef = useRef(noteOn);
  const noteOffRef = useRef(noteOff);
  const pedalChangeRef = useRef(pedalChange);
  useEffect(() => {
    noteOnRef.current = noteOn;
    noteOffRef.current = noteOff;
    pedalChangeRef.current = pedalChange;
  }, [noteOn, noteOff, pedalChange]);

  const [capMidiReceived, setCapMidiReceived] = useState<{
    on: string;
    off: string;
    other: unknown;
    connected: boolean;
  }>({
    on: '--',
    off: '--',
    other: 0,
    connected: false,
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!audioRecognitionEnabled) {
      setAudioInputReady(false);
      setAudioInputChecked(true);
      setPreferredInputMode('midi');
    }
  }, [audioRecognitionEnabled, setPreferredInputMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(INPUT_MODE_STORAGE_KEY, preferredInputMode);
    } catch {
      // Ignore storage failures.
    }
  }, [preferredInputMode]);

  // Whether current route should block for MIDI (not in allowed list and flag enabled)
  const requiresMidiGate = React.useCallback(() => {
    // Don't show the MIDI blocker when the dedicated device compatibility
    // blocker should own the experience instead.
    const detectedDevice = detectDevice();
    const isIneligableDevice =
      !isCapacitorNative() &&
      (detectedDevice === 'ipad' ||
        detectedDevice === 'iphone' ||
        (isSafariBrowser() && detectedDevice === 'web'));

    const path = (location?.pathname || '').replace(/\/$/, ''); // normalize trailing slash
    const isRepertoireCreatePage = path === '/repertoire' && data?.repPageSelected === 'Create';

    // Robust match:
    // - exact match: "/repertoire-play"
    // - subroute match: "/repertoire-play/123"
    const isEligablePath = MIDI_BLOCKER_PATHS.some((p) => {
      const base = (p || '').replace(/\/$/, '');
      return path === base || path.startsWith(base + '/');
    });

    logger.debug('location.pathname', location?.pathname);
    return restrict_midi && !isIneligableDevice && (isEligablePath || isRepertoireCreatePage);
  }, [restrict_midi, location?.pathname, data?.repPageSelected]);

  // Store midiAccess ref for statechange handler
  const midiAccessRef = useRef<MIDIAccess | null>(null);

  useEffect(() => {
    if (isCapacitorNative()) return;
    if (initialized) return;

    let cancelled = false;
    let midiInitSettled = false;
    let midiInitTimeoutId: number | null = null;

    Logger.log('[MidiContext] Starting MIDI initialization with native Web MIDI API...');

    const markMidiDevicesChecked = (reason: string) => {
      if (midiInitSettled || cancelled) return;
      midiInitSettled = true;
      if (midiInitTimeoutId != null) {
        window.clearTimeout(midiInitTimeoutId);
        midiInitTimeoutId = null;
      }
      console.log('[MidiContext] MIDI device check complete:', reason);
      setMidiDevicesChecked(true);
      setInitialized(true);
    };

    // Helper to parse MIDI messages
    const handleMidiMessage = (event: MIDIMessageEvent) => {
      const data = event.data;
      if (!data || data.length < 1) return;

      const status = data[0];
      const command = status & 0xf0;
      const channel = status & 0x0f;
      const note = data.length > 1 ? data[1] : 0;
      const velocity = data.length > 2 ? data[2] : 0;

      // Note On (0x90) with velocity > 0
      if (command === 0x90 && velocity > 0) {
        Logger.log('[MidiContext] Note On:', note, velocity);
        noteOnRef.current({
          note,
          velocity,
          channel,
          command,
          data1: note,
          data2: velocity,
        });
        return;
      }

      // Note Off (0x80) or Note On with velocity 0
      if (command === 0x80 || (command === 0x90 && velocity === 0)) {
        Logger.log('[MidiContext] Note Off:', note);
        noteOffRef.current({
          note,
          velocity,
          channel,
          command,
          data1: note,
          data2: velocity,
        });
        return;
      }

      // Control Change (0xB0), including sustain pedal (CC64)
      if (command === 0xb0) {
        const controller = note;
        const value = velocity;
        if (controller === SUSTAIN_PEDAL_CONTROLLER) {
          const down = value >= SUSTAIN_PEDAL_ON_VALUE;
          console.log('[MidiContext] Sustain Pedal:', down ? 'Down' : 'Up', value);
          pedalChangeRef.current({
            channel,
            controller,
            value,
            down,
            command,
            data1: controller,
            data2: value,
          });
        }
        return;
      }
    };

    const connectDevice = (input: MIDIInput) => {
      // NOTE: We intentionally do NOT check `cancelled` here.
      // Device setup must proceed even if the effect was cleaned up,
      // otherwise MIDI won't work after StrictMode double-mounting.

      const inputId = input.id;
      const inputName = input.name || 'Unknown MIDI Device';

      Logger.log('[MidiContext] connectDevice called for:', inputId, inputName);

      setDevices((prev) => {
        if (prev[inputId]) {
          Logger.log('[MidiContext] Device already exists:', inputId);
          return prev;
        }

        Logger.log('[MidiContext] Adding new device:', inputId, inputName);
        logger.info('ADDING DEVICE', { id: inputId, name: inputName });

        // Set up the MIDI message handler
        input.onmidimessage = handleMidiMessage;

        return {
          ...prev,
          [inputId]: { id: inputId, name: inputName, input },
        };
      });
    };

    const disconnectDevice = (inputId: string) => {
      Logger.log('[MidiContext] ===========DEVICE DISCONNECT===============', inputId);
      logger.info('===========DEVICE DISCONNECT===============', inputId);

      setDevices((prev) => {
        const existing = prev[inputId];
        if (!existing) return prev;

        // Remove the message handler
        try {
          existing.input.onmidimessage = null;
        } catch (e) {
          Logger.log('[MidiContext] Error removing message handler:', e);
        }

        const next = { ...prev };
        delete next[inputId];
        return next;
      });
    };

    (async () => {
      try {
        // Check if Web MIDI API is available
        if (!navigator.requestMIDIAccess) {
          logger.error('[MidiContext] Web MIDI API not available in this browser');
          markMidiDevicesChecked('web-midi-unavailable');
          return;
        }

        logger.log('[MidiContext] Calling navigator.requestMIDIAccess()...');
        logger.log('[MidiContext] Current URL:', window.location.href);
        logger.log('[MidiContext] Is secure context:', window.isSecureContext);
        logger.log('[MidiContext] globalMidiAccess:', globalMidiAccess ? 'exists' : 'null');
        logger.log('[MidiContext] globalMidiPromise:', globalMidiPromise ? 'exists' : 'null');
        logger.info('trying connect');

        // Add a small delay to let browser clean up from previous page
        // This helps prevent the hanging issue on refresh
        logger.log('[MidiContext] Waiting 500ms before requesting MIDI...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (cancelled) {
          logger.log('[MidiContext] Cancelled during delay');
          return;
        }

        // Use global state to prevent multiple requests
        // This fixes the issue where requestMIDIAccess hangs on refresh
        let midiPromise: Promise<MIDIAccess>;

        if (globalMidiAccess) {
          // Already have MIDI access from before
          logger.log('[MidiContext] Using existing globalMidiAccess');
          midiPromise = Promise.resolve(globalMidiAccess);
        } else if (globalMidiPromise) {
          // Request already in progress
          logger.log('[MidiContext] Using existing globalMidiPromise');
          midiPromise = globalMidiPromise;
        } else {
          // Need to request new MIDI access
          logger.log('[MidiContext] Creating new MIDI access request...');
          globalMidiPromise = navigator.requestMIDIAccess({ sysex: false });
          midiPromise = globalMidiPromise;

          // Store the access once it resolves
          midiPromise
            .then((access) => {
              globalMidiAccess = access;
              logger.log('[MidiContext] Stored globalMidiAccess');
            })
            .catch(() => {
              // Clear the promise so we can retry
              globalMidiPromise = null;
            });
        }

        logger.log('[MidiContext] Setting up .then() handler...');

        midiInitTimeoutId = window.setTimeout(() => {
          if (midiInitSettled || cancelled) return;
          console.warn('[MidiContext] MIDI init timed out; allowing audio-recognition fallback');
          reportError(
            new Error('MIDI init timed out after 2500ms; allowing audio-recognition fallback'),
            'midi.timeout',
          );
          markMidiDevicesChecked('web-midi-timeout');
        }, 2500);

        // Handle the MIDI access when/if it resolves (don't block on it)
        midiPromise.then(
          (midiAccess) => {
            // NOTE: We intentionally do NOT check `cancelled` here.
            // Even if the React effect was cleaned up, we still want to:
            // 1. Store the midiAccess globally
            // 2. Set up device handlers
            // This ensures MIDI works even after StrictMode double-mounting

            logger.log('[MidiContext] MIDI access granted! cancelled =', cancelled);
            midiAccessRef.current = midiAccess;

            // Count inputs
            let inputCount = 0;
            midiAccess.inputs.forEach(() => inputCount++);
            logger.log('[MidiContext] midiAccess.inputs count =', inputCount);
            logger.info('in connect');

            // Process initial devices
            logger.log('[MidiContext] Processing initial devices...');
            midiAccess.inputs.forEach((input: MIDIInput) => {
              logger.log('[MidiContext] Found initial input:', input.id, input.name);
              connectDevice(input);
            });

            // Listen for device connect/disconnect
            midiAccess.onstatechange = (event: MIDIConnectionEvent) => {
              const port = event.port;
              logger.log(
                '[MidiContext] onstatechange:',
                port?.type,
                port?.id,
                port?.name,
                port?.state,
              );

              if (port?.type === 'input') {
                if (port.state === 'connected') {
                  logger.log(
                    '[MidiContext] ===========DEVICE CONNECT===============',
                    port.id,
                    port.name,
                  );
                  logger.info('===========DEVICE CONNECT===============');
                  connectDevice(port as MIDIInput);
                  if (!successEventSentRef.current) {
                    dispatch(
                      eventAction.midiConnectionSuccessEventAction() as Parameters<
                        typeof dispatch
                      >[0],
                    );
                    successEventSentRef.current = true;
                  }
                  setShowMidiModal(false);
                } else if (port.state === 'disconnected') {
                  disconnectDevice(port.id);
                }
              }
            };

            // Check if we should show/hide modal based on devices
            let deviceCount = 0;
            midiAccess.inputs.forEach(() => deviceCount++);
            const hasAny = deviceCount > 0;
            logger.log('[MidiContext] hasAny devices:', hasAny);

            // Mark that we've checked for devices (prevents modal flash)
            markMidiDevicesChecked('web-midi-ready');

            if (hasAny) {
              logger.log('[MidiContext] Devices found, hiding modal');
              setShowMidiModal(false);
            } else if (requiresMidiGate()) {
              logger.log('[MidiContext] No devices, showing modal');
              setShowMidiModal(true);
            }

            logger.log('[MidiContext] MIDI initialization complete (async)');
          },
          (err) => {
            logger.error('[MidiContext] MIDI access denied or failed:', err);
            reportError(err, 'midi.access');
            markMidiDevicesChecked('web-midi-failed');
          },
        );

        // Don't wait - let the app continue
        // The MIDI will connect in the background when/if the browser allows it
        logger.log('[MidiContext] Continuing without waiting for MIDI...');

        // DON'T show modal here immediately - wait for the .then() callback
        // to complete and check if devices were found. This prevents the
        // modal from flashing briefly before MIDI is detected.

        // All MIDI handling is done in the .then() callback above
        // Nothing else to do here - the effect will complete and
        // MIDI will connect asynchronously in the background
      } catch (e) {
        logger.error('[MidiContext] requestMIDIAccess() failed:', e);
        reportError(e, 'midi.exception');
        markMidiDevicesChecked('web-midi-exception');
      } finally {
        // Initialization is marked settled through markMidiDevicesChecked().
        // That keeps refresh/remount flows from prematurely short-circuiting
        // before Web MIDI access has actually resolved or timed out.
      }
    })();

    return () => {
      logger.log('[MidiContext] Cleanup running, setting cancelled = true');
      cancelled = true;
      if (midiInitTimeoutId != null) {
        window.clearTimeout(midiInitTimeoutId);
      }
      // Keep the Web MIDI hot-plug handler attached across route changes and other provider
      // re-renders. Tearing it down here breaks auto-close behavior for the MIDI blocker after
      // navigation because initialization is already considered complete and the handler is not
      // reattached.
    };
  }, [audioRecognitionEnabled, initialized, dispatch]);

  // Define before first usage to avoid TDZ errors
  const isAnyDeviceConnected = React.useCallback(() => {
    return isCapacitorNative() ? !!capMidiReceived.connected : Object.keys(devices).length > 0;
  }, [capMidiReceived.connected, devices]);

  const canUseAcousticInput = audioRecognitionEnabled;
  const acousticModeAvailable = canUseAcousticInput && preferredInputMode === 'audio';
  const isAnyInputConnected = React.useCallback(() => {
    return (
      isAnyDeviceConnected() ||
      (acousticModeAvailable && audioInputReady && calibrationProfile != null)
    );
  }, [acousticModeAvailable, audioInputReady, calibrationProfile, isAnyDeviceConnected]);

  const isMidiGateSatisfied = React.useCallback(() => {
    return audioRecognitionEnabled ? isAnyInputConnected() : isAnyDeviceConnected();
  }, [audioRecognitionEnabled, isAnyDeviceConnected, isAnyInputConnected]);

  const shouldUseAudioRecognitionInput = acousticModeAvailable;
  const audioInputLatencyMs =
    shouldUseAudioRecognitionInput && audioInputReady && calibrationProfile != null
      ? AUDIO_RECOGNITION_INPUT_LATENCY_MS
      : 0;
  const shouldKeepAudioCalibrationModalOpen =
    showMidiModal &&
    audioRecognitionEnabled &&
    preferredInputMode === 'audio' &&
    (calibration.running || calibration.phase === 'analyzing' || calibration.phase === 'complete');
  const shouldKeepAudioSetupModalOpen =
    showMidiModal &&
    audioRecognitionEnabled &&
    preferredInputMode === 'audio' &&
    calibrationProfile == null;
  const shouldKeepInputSetupModalOpen =
    inputSetupModalPinnedOpen ||
    shouldKeepAudioCalibrationModalOpen ||
    shouldKeepAudioSetupModalOpen;
  const shouldHoldAudioCalibrationCompleteModal =
    shouldKeepAudioCalibrationModalOpen && calibration.phase === 'complete';

  useEffect(() => {
    if (!shouldDebugMidiGate()) return;

    const gateRequired = requiresMidiGate();
    const anyDeviceConnected = isAnyDeviceConnected();
    const anyInputConnected = isMidiGateSatisfied();

    let blockerDecision = 'hidden:unknown';
    if (!gateRequired) {
      blockerDecision = 'hidden:route-not-gated';
    } else if (!isCapacitorNative() && !midiDevicesChecked) {
      blockerDecision = 'pending:midi-device-check';
    } else if (anyInputConnected) {
      blockerDecision = 'hidden:input-connected';
    } else if (dismissedMidiModal) {
      blockerDecision = 'hidden:dismissed';
    } else if (showMidiModal) {
      blockerDecision = 'shown';
    } else {
      blockerDecision = 'eligible:should-show';
    }

    console.log('[MidiContext][gateState]', {
      path: location?.pathname,
      repPageSelected: data?.repPageSelected ?? null,
      preferredInputMode,
      storedUserPreference: data?.userData?.prefer_audio_recognition_input ?? null,
      gateRequired,
      midiDevicesChecked,
      deviceCount: Object.keys(devices).length,
      anyDeviceConnected,
      audioRecognitionEnabled,
      acousticModeAvailable,
      audioInputReady,
      hasCalibrationProfile: calibrationProfile != null,
      shouldUseAudioRecognitionInput,
      anyInputConnected,
      dismissedMidiModal,
      showMidiModal,
      blockerDecision,
    });
  }, [
    acousticModeAvailable,
    audioInputReady,
    audioRecognitionEnabled,
    calibration.phase,
    calibrationProfile,
    data?.repPageSelected,
    data?.userData?.prefer_audio_recognition_input,
    devices,
    dismissedMidiModal,
    isAnyDeviceConnected,
    isMidiGateSatisfied,
    location?.pathname,
    midiDevicesChecked,
    preferredInputMode,
    requiresMidiGate,
    shouldUseAudioRecognitionInput,
    showMidiModal,
  ]);

  // Also hide modal when navigating to an allowed path
  useEffect(() => {
    if (showMidiModal && !requiresMidiGate()) {
      setInputSetupModalPinnedOpen(false);
      setShowMidiModal(false);
    }
  }, [location.pathname, showMidiModal, requiresMidiGate]);

  // Auto-hide modal as soon as a device is connected
  useEffect(() => {
    if (showMidiModal && isMidiGateSatisfied()) {
      if (shouldKeepInputSetupModalOpen) return;
      setInputSetupModalPinnedOpen(false);
      setShowMidiModal(false);
    }
  }, [showMidiModal, isMidiGateSatisfied, shouldKeepInputSetupModalOpen]);

  useEffect(() => {
    if (!shouldHoldAudioCalibrationCompleteModal || !isMidiGateSatisfied()) return;
    const timeoutId = window.setTimeout(() => {
      setInputSetupModalPinnedOpen(false);
      setShowMidiModal(false);
    }, AUDIO_CALIBRATION_COMPLETE_MODAL_CLOSE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isMidiGateSatisfied, shouldHoldAudioCalibrationCompleteModal]);

  useEffect(() => {
    const onDevicesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      Logger.log('MIDI devices changed:', detail);
      const hasDevices = detail?.sources?.length > 0;
      Logger.log('[MidiContext] MIDI devices detected:', hasDevices, 'sources:', detail?.sources);
      setCapMidiReceived((prev) => ({ ...prev, connected: hasDevices }));
    };

    window.addEventListener('MIDIDevicesChanged', onDevicesChanged);
    document.addEventListener('MIDIDevicesChanged', onDevicesChanged as EventListener);

    // For Capacitor: Check for already-stored device state on mount
    // This ensures we detect devices that were already connected before React mounted
    if (isCapacitorNative()) {
      Logger.log('[MidiContext] Checking for initial MIDI device state...');
      const storedState = (window as Window & { __midiDeviceState?: unknown }).__midiDeviceState;
      if (storedState) {
        const hasDevices =
          typeof storedState.connected === 'boolean'
            ? storedState.connected
            : (storedState.sources?.length ?? 0) > 0;

        Logger.log('[MidiContext] Found stored MIDI device state:', storedState);
        setCapMidiReceived((prev) => ({ ...prev, connected: hasDevices }));
      }
      // Mark devices as checked for Capacitor (prevents modal flash)
      setMidiDevicesChecked(true);
    }

    return () => {
      window.removeEventListener('MIDIDevicesChanged', onDevicesChanged);
      document.removeEventListener('MIDIDevicesChanged', onDevicesChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isCapacitorNative()) return; // function designed for capacitor.

    Logger.log('Adding listener for MIDIReceived...');
    Logger.log('What is the midi ?', data?.userData);

    const handleMidiReceived = (event: MIDIMessageEvent) => {
      const raw = Array.from(event?.detail?.data ?? []) as number[];
      if (!raw.length) return;

      // Set "connected" only when it flips from false -> true (prevents state storm)
      setCapMidiReceived((prev) => (prev.connected ? prev : { ...prev, connected: true }));

      // Helpers
      const REALTIME_MIN = 0xf8; // F8..FF
      const isRealtime = (b: number) => b >= REALTIME_MIN; // includes FA/FB/FC transport
      const isStatus = (b: number) => b >= 0x80 && b < 0xf0; // channel voice only
      const msgDataLen = (status: number) => {
        const hi = status & 0xf0;
        // Cn/Dn have 1 data byte; others (8n,9n,An,Bn,En) have 2
        return hi === 0xc0 || hi === 0xd0 ? 1 : 2;
      };

      // Persist running status across packets using a window property (no extra globals needed)
      let runningStatus: number | null =
        (window as Window & { __midiRunningStatus?: number | null }).__midiRunningStatus ?? null;

      // Fast path: a single real-time byte? ignore it entirely.
      if (raw.length === 1 && isRealtime(raw[0])) return;

      // Parse stream
      for (let i = 0; i < raw.length; ) {
        const b: number = raw[i];

        // Skip any real-time byte (they can appear between data bytes)
        if (isRealtime(b)) {
          i += 1;
          continue;
        }

        // New status byte?
        if (isStatus(b)) {
          runningStatus = b;
          (window as Window & { __midiRunningStatus?: number | null }).__midiRunningStatus =
            runningStatus; // stash for next callback
          i += 1;

          const needed = msgDataLen(runningStatus);
          if (i + needed > raw.length) {
            // Incomplete message in this packet; wait for next one
            break;
          }

          const d1 = raw[i];
          const d2 = needed === 2 ? raw[i + 1] : 0;

          handleChannelMessage(runningStatus, d1, d2);
          i += needed;
          continue;
        }

        // Data byte with running status
        if (runningStatus !== null) {
          const needed = msgDataLen(runningStatus);
          // We already have the first data byte at raw[i]
          if (needed === 2 && i + 1 >= raw.length) {
            // Incomplete; wait for the next packet to get the second data byte
            break;
          }
          const d1 = raw[i];
          const d2 = needed === 2 ? raw[i + 1] : 0;

          handleChannelMessage(runningStatus, d1, d2);
          i += needed;
          continue;
        }

        // Stray data byte with no running status — skip it
        i += 1;
      }

      function handleChannelMessage(status: number, data1: number, data2: number) {
        // NOTE ON (9n) with velocity > 0
        if ((status & 0xf0) === 0x90 && data2 > 0) {
          // NOTE: Do NOT write a per-note debug string into capMidiReceived
          // state here — it re-renders the whole MidiProvider subtree (incl.
          // the tutorial video player) on every note and stalls the video on
          // iPad during interactive video tutorials (MUS-280).
          // Logger.log(`Note-On detected: Note ${data1}, Velocity ${data2}`);
          noteOnRef.current?.({
            channel: status & 0x0f,
            command: status & 0xf0,
            note: data1,
            velocity: data2,
            data1,
            data2,
          });
          return;
        }

        // NOTE OFF (8n) or Note-On with velocity 0
        if ((status & 0xf0) === 0x80 || ((status & 0xf0) === 0x90 && data2 === 0)) {
          // See note above: avoid per-note capMidiReceived state writes that
          // re-render the tutorial video and cause it to stall (MUS-280).
          // Logger.log(`Note-Off detected: Note ${data1}, Velocity ${data2}`);
          noteOffRef.current?.({
            channel: status & 0x0f,
            command: status & 0xf0,
            note: data1,
            velocity: data2,
            data1,
            data2,
          });
          return;
        }

        // Control Change (Bn)
        if ((status & 0xf0) === 0xb0) {
          logger.log(`Control Change: Controller ${data1}, Value ${data2}`);
          if (data1 === SUSTAIN_PEDAL_CONTROLLER) {
            pedalChangeRef.current?.({
              channel: status & 0x0f,
              controller: data1,
              value: data2,
              down: data2 >= SUSTAIN_PEDAL_ON_VALUE,
              command: status & 0xf0,
              data1,
              data2,
            });
          }
          return;
        }
        // Program Change (Cn) or Channel Pressure (Dn) handled as “unhandled” for now
        if ((status & 0xf0) === 0xc0 || (status & 0xf0) === 0xd0) {
          logger.log(`Channel msg ${status.toString(16)}: d1=${data1}`);
          return;
        }
        logger.log(`Unhandled MIDI message: [${status}, ${data1}, ${data2}]`);
      }
    };

    document.addEventListener('MIDIReceived', handleMidiReceived as EventListener);

    return () => {
      logger.debug('Removing listener for MIDIReceived...');
      document.removeEventListener('MIDIReceived', handleMidiReceived as EventListener);
    };
  }, []);

  useEffect(() => {
    setMidiInitialized(false);
  }, [data?.userData?.audio_on]);

  useEffect(() => {
    if (data?.userData?.audio_on === false) {
      sustainedNotesRef.current.clear();
      activeNotesRef.current.clear();
      sustainPedalDownRef.current = false;
    }
  }, [data?.userData?.audio_on]);

  useEffect(() => {
    if ((midiInitialized || isMidiGateSatisfied()) && !successEventSentRef.current) {
      dispatch(eventAction.midiConnectionSuccessEventAction() as Parameters<typeof dispatch>[0]);
      successEventSentRef.current = true;
    }
  }, [dispatch, isMidiGateSatisfied, midiInitialized]);

  useEffect(() => {
    if (!isCapacitorNative()) return;

    const pianoEnabled = data?.userDataFetched === true && data?.userData?.audio_on !== false;
    MuseFlowAudio.setPianoEnabled({ enabled: pianoEnabled });
  }, [data?.userDataFetched, data?.userData?.audio_on]);

  const addListener = React.useCallback(
    (listener: Listener) => {
      Logger.log(
        '[MidiContext] addListener:',
        listener.name,
        'current size:',
        listeners.current.size,
      );
      logger.info('adding listener', listener);
      if (listeners.current.has(listener.name)) {
        Logger.log('[MidiContext] Replacing existing listener:', listener.name);
      }
      listeners.current.delete(listener.name);
      listeners.current.set(listener.name, listener);
      Logger.log('[MidiContext] After add, size:', listeners.current.size);
    },
    [listeners],
  );

  const removeListener = React.useCallback(
    (listener: Listener) => {
      Logger.log('[MidiContext] removeListener:', listener.name);
      listeners.current.delete(listener.name);
      Logger.log('[MidiContext] After remove, size:', listeners.current.size);
    },
    [listeners],
  );

  const clearListeners = React.useCallback(() => {
    logger.info('clearing listeners');
    for (const [key] of listeners.current) {
      listeners.current.delete(key);
    }
  }, [listeners.current]);

  const isConnected = React.useCallback(() => {
    return isMidiGateSatisfied();
  }, [isMidiGateSatisfied]);

  const checkMidiAndShowModal = React.useCallback(() => {
    // Don't show modal until we've confirmed MIDI device state.
    // On page reload, MIDI init is async — without this guard
    // the modal flashes briefly (no devices yet) then auto-hides
    // once MIDI resolves, disrupting the exercise setup flow.
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    logger.log('checking midi and show modal', !isMidiGateSatisfied() && requiresMidiGate());
    if (!isMidiGateSatisfied() && requiresMidiGate()) {
      setShowMidiModal(true);
    }
  }, [midiDevicesChecked, isMidiGateSatisfied, requiresMidiGate]);

  const openInputSetupModal = React.useCallback(() => {
    setDismissedMidiModal(false);
    setInputSetupModalPinnedOpen(true);
    setShowMidiModal(true);
  }, []);

  useEffect(() => {
    // Don't show modal until we've checked for MIDI devices (prevents flash)
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    if (!requiresMidiGate()) {
      if (showMidiModal) {
        setInputSetupModalPinnedOpen(false);
        setShowMidiModal(false);
      }
      return;
    }
    if (isMidiGateSatisfied()) {
      if (shouldKeepInputSetupModalOpen) return;
      if (showMidiModal) {
        setInputSetupModalPinnedOpen(false);
        setShowMidiModal(false);
      }
      return;
    }
    if (dismissedMidiModal) return;
    dispatch(eventAction.midiConnectionFailureEventAction() as Parameters<typeof dispatch>[0]);
    if (
      location.pathname !== '/tutorial' ||
      data?.levelData[data?.levelSelect]?.level_number != 0
    ) {
      setShowMidiModal(true);
    }
  }, [
    location.pathname,
    requiresMidiGate,
    isMidiGateSatisfied,
    dismissedMidiModal,
    showMidiModal,
    dispatch,
    data?.levelData,
    data?.levelSelect,
    midiDevicesChecked,
    shouldKeepInputSetupModalOpen,
  ]);

  const hideMidiModal = React.useCallback(() => {
    setInputSetupModalPinnedOpen(false);
    setShowMidiModal(false);
  }, []);

  useEffect(() => {
    if (showMidiModal && location.pathname !== '/tutorial') {
      Logger.log('??????????????????????????????');
      setTimeout(() => {
        // need to wait for the scene to load before stopping playback
        Logger.log('showing midi modal, stopping playback');
        dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
        console.log('[LOOP-DBG] setRepertoirePlaying(false) from MidiContext showMidiModal');
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        dispatch(tutorialActions.setManualPause({ manualPause: true }));
      }, 1000);
    }
  }, [showMidiModal, location.pathname]);

  // Reset dismissal when navigating to a different path so gate can appear again.
  useEffect(() => {
    setDismissedMidiModal(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isCapacitorNative()) return;
    Logger.log('devices', devices);
    if (Object.keys(devices).length === 0) setDismissedMidiModal(false);
  }, [devices]);

  // On route change, enforce MIDI gate: if the new page requires MIDI and no device is connected, show the modal.
  useEffect(() => {
    // Don't show modal until we've checked for MIDI devices (prevents flash)
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    if (requiresMidiGate()) {
      if (!isMidiGateSatisfied()) {
        if (!showMidiModal && !dismissedMidiModal) {
          dispatch(
            eventAction.midiConnectionFailureEventAction() as Parameters<typeof dispatch>[0],
          );
          // level 0 handles its own midi blocker page, which fires after the first chapter,
          // in order to give new users a chance to see what the app is like before being blocked
          // by the midi gate.
          if (
            location.pathname !== '/tutorial' ||
            data?.levelData[data?.levelSelect]?.level_number != 0
          ) {
            setShowMidiModal(true);
          }
        }
      } else if (showMidiModal && !shouldKeepInputSetupModalOpen) {
        setInputSetupModalPinnedOpen(false);
        setShowMidiModal(false);
      }
    } else if (showMidiModal) {
      setInputSetupModalPinnedOpen(false);
      setShowMidiModal(false);
    }
  }, [
    location.pathname,
    requiresMidiGate,
    isMidiGateSatisfied,
    showMidiModal,
    dispatch,
    dismissedMidiModal,
    midiDevicesChecked,
    shouldKeepInputSetupModalOpen,
  ]);

  const isRepertoirePlayModal = location?.pathname?.includes('/repertoire-play') ?? false;

  return (
    <MidiContext.Provider
      value={{
        addListener,
        removeListener,
        clearListeners,
        isConnected,
        capMidiReceived: capMidiReceived,
        checkMidiAndShowModal,
        openInputSetupModal,
        hideMidiModal,
        showMidiModal,
        dismissedMidiModal, // expose in context
        audioInputLatencyMs,
        preferredInputMode,
        setPreferredInputMode,
      }}
    >
      <AudioRecognitionMidiBridge
        enabled={shouldUseAudioRecognitionInput && calibrationProfile != null}
        noteOnRef={noteOnRef}
        noteOffRef={noteOffRef}
        setAudioInputReady={setAudioInputReady}
        setAudioInputChecked={setAudioInputChecked}
      />
      {children}
      {showMidiModal && !audioRecognitionEnabled && (
        <>
          {/* Transparent backdrop to block clicks but still show app underneath */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(4,11,19,0.78)',
              zIndex: 3000,
            }}
          />
          {/* Back to roadmap button */}

          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3001,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 'min(900px, 90vw)',
                minHeight: '32vh',
                borderRadius: '8px',
                background: 'transparent',
                px: { xs: 4, md: 7 },
                py: { xs: 6, md: 8 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  background: 'rgba(4, 11, 19, 0.92)',
                  borderRadius: '12px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 2.5, md: 3 },
                  mb: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <Box sx={{ position: 'fixed', top: 12, left: 12, zIndex: 3002 }}>
                  <IconButton
                    aria-label={
                      location?.pathname?.includes('/repertoire-play')
                        ? 'Back to repertoire'
                        : 'Back to roadmap'
                    }
                    onClick={() => {
                      const dest = location?.pathname?.includes('/repertoire-play')
                        ? '/repertoire'
                        : '/roadmap';
                      Logger.log(`[MUS-61] MidiContext disconnect button -> ${dest}`);
                      navigate(dest);
                    }}
                    sx={{
                      color: '#CED0D4',
                      backgroundColor: 'rgba(4,11,19,0.6)',
                      '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Box>
                <Box sx={{ position: 'fixed', top: 12, right: 12, zIndex: 3002 }}>
                  <IconButton
                    aria-label="Close"
                    onClick={() => {
                      setShowMidiModal(false);
                      setDismissedMidiModal(true);
                    }}
                    sx={{
                      color: '#CED0D4',
                      backgroundColor: 'rgba(4,11,19,0.6)',
                      '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '28px', md: '36px' },
                    color: '#CED0D4',
                    textAlign: 'center',
                  }}
                >
                  Connect your MIDI keyboard
                </Box>
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '18px', md: '20px' },
                    color: '#ADB1B7',
                    textAlign: 'center',
                    maxWidth: 760,
                    lineHeight: 1.5,
                  }}
                >
                  We didn't detect a MIDI device. Plug in and turn on your keyboard. This message
                  will close automatically once connected.
                </Box>
                <Box sx={{ height: { xs: '180px', md: '240px' }, mt: 1 }}>
                  <img style={{ maxHeight: '100%' }} src={KeyboardConnectMidi} alt="connect-midi" />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
      {showMidiModal && audioRecognitionEnabled && (
        <>
          {/* Transparent backdrop to block clicks but still show app underneath */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(4,11,19,0.78)',
              zIndex: 3000,
            }}
          />
          {/* Back to roadmap button */}

          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3001,
              padding: '24px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: isRepertoirePlayModal ? 'min(960px, 94vw)' : 'min(1120px, 92vw)',
                maxHeight: isRepertoirePlayModal ? '84vh' : '88vh',
                borderRadius: isRepertoirePlayModal ? '18px' : '22px',
                background:
                  'linear-gradient(180deg, rgba(8, 16, 28, 0.98) 0%, rgba(5, 10, 18, 0.99) 100%)',
                border: '1px solid rgba(121, 216, 255, 0.16)',
                boxShadow: isRepertoirePlayModal
                  ? '0 18px 58px rgba(0, 0, 0, 0.44)'
                  : '0 28px 90px rgba(0, 0, 0, 0.48)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ position: 'absolute', top: 14, left: 14, zIndex: 3002 }}>
                <IconButton
                  aria-label={
                    location?.pathname?.includes('/repertoire-play')
                      ? 'Back to repertoire'
                      : 'Back to roadmap'
                  }
                  onClick={() => {
                    const dest = location?.pathname?.includes('/repertoire-play')
                      ? '/repertoire'
                      : '/roadmap';
                    console.log(`[MUS-61] MidiContext disconnect button → ${dest}`);
                    setInputSetupModalPinnedOpen(false);
                    navigate(dest);
                  }}
                  sx={{
                    color: '#CED0D4',
                    backgroundColor: 'rgba(4,11,19,0.6)',
                    '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
              <Box sx={{ position: 'absolute', top: 14, right: 14, zIndex: 3002 }}>
                <IconButton
                  aria-label="Close"
                  onClick={() => {
                    setInputSetupModalPinnedOpen(false);
                    setShowMidiModal(false);
                    setDismissedMidiModal(true);
                  }}
                  sx={{
                    color: '#CED0D4',
                    backgroundColor: 'rgba(4,11,19,0.6)',
                    '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: isRepertoirePlayModal
                    ? { xs: '64px 16px 18px', md: '66px 24px 22px' }
                    : { xs: '72px 18px 20px', md: '76px 28px 24px' },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(121, 216, 255, 0.4) rgba(255,255,255,0.06)',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(121, 216, 255, 0.4)',
                    borderRadius: '999px',
                  },
                }}
              >
                <Box
                  sx={{
                    fontWeight: 700,
                    fontSize: isRepertoirePlayModal
                      ? { xs: '24px', md: '28px' }
                      : { xs: '28px', md: '40px' },
                    color: '#F2F6FF',
                    textAlign: 'center',
                    letterSpacing: 0,
                    marginBottom: isRepertoirePlayModal ? '8px' : '12px',
                  }}
                >
                  Choose Your Piano Input
                </Box>
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: isRepertoirePlayModal
                      ? { xs: '14px', md: '15px' }
                      : { xs: '16px', md: '19px' },
                    color: 'rgba(219, 228, 247, 0.78)',
                    textAlign: 'center',
                    maxWidth: isRepertoirePlayModal ? 720 : 860,
                    lineHeight: 1.5,
                    marginX: 'auto',
                    marginBottom: isRepertoirePlayModal ? '18px' : '26px',
                  }}
                >
                  We didn’t detect a MIDI keyboard. You can connect MIDI as usual, or switch to
                  acoustic piano input and calibrate your microphone for note recognition.
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: isRepertoirePlayModal ? '10px' : '14px',
                    flexWrap: 'wrap',
                    marginBottom: isRepertoirePlayModal ? '16px' : '20px',
                  }}
                >
                  <Button
                    variant={preferredInputMode === 'midi' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setPreferredInputMode('midi');
                    }}
                    sx={{
                      minWidth: isRepertoirePlayModal
                        ? { xs: '180px', md: '200px' }
                        : { xs: '220px', md: '250px' },
                      minHeight: isRepertoirePlayModal ? '46px' : '56px',
                      borderRadius: isRepertoirePlayModal ? '10px' : '12px',
                      fontSize: isRepertoirePlayModal ? '14px' : '15px',
                      fontWeight: 700,
                      textTransform: 'none',
                      letterSpacing: 0,
                      borderWidth: preferredInputMode === 'midi' ? '2px' : '1px',
                      borderColor: preferredInputMode === 'midi' ? '#6F87F1' : '#767C8C',
                      backgroundColor:
                        preferredInputMode === 'midi' ? '#243056' : 'rgba(255,255,255,0.02)',
                      color: '#ECECED',
                      boxShadow:
                        preferredInputMode === 'midi'
                          ? '0 10px 22px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(111, 135, 241, 0.24)'
                          : 'none',
                      opacity: preferredInputMode === 'midi' ? 1 : 0.9,
                      '&:hover': {
                        borderColor: '#6F87F1',
                        backgroundColor:
                          preferredInputMode === 'midi' ? '#243056' : 'rgba(111, 135, 241, 0.08)',
                      },
                    }}
                  >
                    MIDI Keyboard
                  </Button>
                  <Button
                    variant={preferredInputMode === 'audio' ? 'contained' : 'outlined'}
                    disabled={!canUseAcousticInput}
                    onClick={() => {
                      if (!canUseAcousticInput) return;
                      setPreferredInputMode('audio');
                    }}
                    sx={{
                      minWidth: isRepertoirePlayModal
                        ? { xs: '180px', md: '200px' }
                        : { xs: '220px', md: '250px' },
                      minHeight: isRepertoirePlayModal ? '46px' : '56px',
                      borderRadius: isRepertoirePlayModal ? '10px' : '12px',
                      fontSize: isRepertoirePlayModal ? '14px' : '15px',
                      fontWeight: 700,
                      textTransform: 'none',
                      letterSpacing: 0,
                      borderWidth: preferredInputMode === 'audio' ? '2px' : '1px',
                      borderColor: preferredInputMode === 'audio' ? '#6F87F1' : '#767C8C',
                      backgroundColor:
                        preferredInputMode === 'audio' ? '#243056' : 'rgba(255,255,255,0.02)',
                      color: '#ECECED',
                      boxShadow:
                        preferredInputMode === 'audio'
                          ? '0 10px 22px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(111, 135, 241, 0.24)'
                          : 'none',
                      opacity: preferredInputMode === 'audio' ? 1 : 0.9,
                      '&:hover': {
                        borderColor: '#6F87F1',
                        backgroundColor:
                          preferredInputMode === 'audio' ? '#243056' : 'rgba(111, 135, 241, 0.08)',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'rgba(118, 124, 140, 0.45)',
                        color: 'rgba(173, 177, 183, 0.6)',
                      },
                    }}
                  >
                    Acoustic Piano
                  </Button>
                </Box>

                <Box
                  sx={{
                    maxWidth: isRepertoirePlayModal ? 780 : 940,
                    marginX: 'auto',
                    borderRadius: isRepertoirePlayModal ? '16px' : '22px',
                    background:
                      'linear-gradient(180deg, rgba(12, 18, 29, 0.96) 0%, rgba(7, 11, 19, 0.99) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: isRepertoirePlayModal
                      ? '0 12px 30px rgba(0, 0, 0, 0.24)'
                      : '0 18px 42px rgba(0, 0, 0, 0.28)',
                    padding: isRepertoirePlayModal
                      ? { xs: '16px', md: '18px 20px' }
                      : { xs: '18px', md: '24px 26px 22px' },
                  }}
                >
                  {preferredInputMode === 'midi' ? (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: '16px',
                        }}
                      >
                        <Box
                          sx={{
                            padding: '7px 14px',
                            borderRadius: '999px',
                            backgroundColor: isAnyDeviceConnected()
                              ? 'rgba(125, 255, 165, 0.14)'
                              : 'rgba(255,255,255,0.08)',
                            color: isAnyDeviceConnected() ? '#7DFFA5' : '#D7E2FF',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {isAnyDeviceConnected() ? 'Connected' : 'Waiting for MIDI'}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          color: '#F2F6FF',
                          fontSize: isRepertoirePlayModal
                            ? { xs: '19px', md: '21px' }
                            : { xs: '22px', md: '28px' },
                          fontWeight: 700,
                          textAlign: 'center',
                          marginBottom: isRepertoirePlayModal ? '8px' : '10px',
                        }}
                      >
                        Connect your MIDI keyboard
                      </Box>
                      <Box
                        sx={{
                          color: 'rgba(219, 228, 247, 0.76)',
                          fontSize: isRepertoirePlayModal ? '14px' : '15px',
                          lineHeight: isRepertoirePlayModal ? 1.45 : 1.6,
                          textAlign: 'center',
                          maxWidth: isRepertoirePlayModal ? 660 : 720,
                          marginX: 'auto',
                          marginBottom: isRepertoirePlayModal ? '14px' : '20px',
                        }}
                      >
                        Plug in and power on your MIDI keyboard. This blocker closes automatically
                        as soon as a keyboard is detected.
                      </Box>
                      <Box
                        sx={{
                          borderRadius: '18px',
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          padding: isRepertoirePlayModal
                            ? { xs: '12px', md: '14px' }
                            : { xs: '14px', md: '20px' },
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          minHeight: isRepertoirePlayModal
                            ? { xs: '150px', md: '178px' }
                            : { xs: '200px', md: '260px' },
                        }}
                      >
                        <Box
                          component="img"
                          src={KeyboardConnectMidi}
                          alt="Connect a MIDI keyboard"
                          sx={{
                            maxWidth: '100%',
                            maxHeight: isRepertoirePlayModal
                              ? { xs: '138px', md: '164px' }
                              : { xs: '190px', md: '240px' },
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          color: '#F2F6FF',
                          fontSize: isRepertoirePlayModal
                            ? { xs: '19px', md: '21px' }
                            : { xs: '22px', md: '28px' },
                          fontWeight: 700,
                          textAlign: 'center',
                          marginBottom: isRepertoirePlayModal ? '8px' : '10px',
                        }}
                      >
                        Set up your acoustic piano input
                      </Box>
                      <Box
                        sx={{
                          color: 'rgba(219, 228, 247, 0.76)',
                          fontSize: isRepertoirePlayModal ? '14px' : '15px',
                          lineHeight: isRepertoirePlayModal ? 1.45 : 1.6,
                          textAlign: 'center',
                          maxWidth: isRepertoirePlayModal ? 680 : 760,
                          marginX: 'auto',
                          marginBottom: isRepertoirePlayModal ? '14px' : '18px',
                        }}
                      >
                        Use your acoustic grand through the microphone. We’ll calibrate to your
                        room, piano, and mic so note recognition is biased toward your actual setup.
                      </Box>
                      <MiddleCReference />
                      <CalibrationPanel
                        sx={{
                          margin: '12px 0 0 0',
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </MidiContext.Provider>
  );
};
